import { useDailyVisitInfo } from 'pages/WaitingRoom/daily/hooks/useDailyVisitInfo'
import { useVisitCallEventDataRef } from './useVisitCallEventDataRef'
import { type Visit } from 'types/types'
import { useEffect } from 'react'
import { useRemoteSessionId } from 'pages/WaitingRoom/daily/hooks/useRemoteSessionId'
import { trackPatientVisitEvent, trackProviderVisitEvent } from '../utils'
import { PROVIDER_EVENT_NAMES } from 'lib/segment/events'
import { PROVIDER_PATIENT_SOURCE_EVENT_NAMES } from '@quickmd-llc/frontend-utils/events'
import { usePreviousValueAutoUpdatingRef } from 'pages/WaitingRoom/daily/hooks/usePreviousValueAutoUpdatingRef'
import { usePatientConnected } from 'pages/WaitingRoom/daily/hooks/usePatientConnected'

/**
 * NOTE: some daily events are tracked outside of useTrackDailySegmentEvents as well. But this handles
 * all the cases in which the current tracking events are twilio specific/do not work for daily.
 */
export function useTrackDailySegmentEvents(visit: Partial<Visit>) {
  const eventDataRef = useVisitCallEventDataRef(visit)

  const dailyVisitInfo = useDailyVisitInfo()

  const isVideoCall = !!dailyVisitInfo?.isVideoCall
  const isPhoneCall = !!dailyVisitInfo?.isPhoneCall

  const { remoteSessionId } = useRemoteSessionId()
  const { dialoutAnswered } = usePatientConnected()

  // The dial-out participant joins while the phone is still ringing, so remoteSessionId alone
  // doesn't mean the patient picked up. Daily's `dialout-answered` does; it resets on
  // `dialout-stopped`, so a retried call that is answered fires again.
  const patientAnsweredPhoneCall = isPhoneCall && dialoutAnswered && !!remoteSessionId

  // Provider Started Video Call
  useEffect(() => {
    if (isVideoCall) {
      trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PROVIDER_STARTED_VIDEO_CALL, eventDataRef.current)
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      trackProviderVisitEvent(PROVIDER_EVENT_NAMES.START_VIDEO_CALL, eventDataRef.current)
    }
  }, [isVideoCall, eventDataRef])

  // Patient Connected to Video Call
  useEffect(() => {
    if (isVideoCall && remoteSessionId) {
      trackPatientVisitEvent(
        PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PATIENT_CONNECTED_TO_VIDEO_CALL_CAPITAL_TO,
        eventDataRef.current,
      )
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      trackProviderVisitEvent(PROVIDER_EVENT_NAMES.PATIENT_CONNECTED_TO_VIDEO_CALL_LOWERCASE_TO, eventDataRef.current)
    }
  }, [isVideoCall, remoteSessionId, eventDataRef])

  const previousSessionIdRef = usePreviousValueAutoUpdatingRef(remoteSessionId)

  // Patient Disconnected from Phone or Video Call
  useEffect(() => {
    if (!remoteSessionId && previousSessionIdRef.current) {
      if (isVideoCall) {
        trackPatientVisitEvent(
          PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PATIENT_DISCONNECTED_FROM_VIDEO_CALL,
          eventDataRef.current,
        )
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        trackProviderVisitEvent(PROVIDER_EVENT_NAMES.PATIENT_DISCONNECTED_FROM_VIDEO_CALL, eventDataRef.current)
      } else {
        trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PHONE_CALL_DISCONNECTED, eventDataRef.current)
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        trackProviderVisitEvent(PROVIDER_EVENT_NAMES.PHONE_CALL_DISCONNECTED, eventDataRef.current)
      }
    }
  }, [remoteSessionId, previousSessionIdRef, eventDataRef, isVideoCall])

  // Provider Started Phone Call
  useEffect(() => {
    if (isPhoneCall) {
      trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PROVIDER_STARTED_PHONE_CALL, eventDataRef.current)
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      trackProviderVisitEvent(PROVIDER_EVENT_NAMES.START_PHONE_CALL, eventDataRef.current)
    }
  }, [isPhoneCall, eventDataRef])

  // Patient Connected To Phone Call
  useEffect(() => {
    if (!patientAnsweredPhoneCall) return
    const eventData = eventDataRef.current
    trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.PATIENT_CONNECTED_TO_PHONE_CALL, eventData)
    const { patient } = eventData
    if (patient) {
      trackProviderVisitEvent(PROVIDER_EVENT_NAMES.PATIENT_CONNECTED_TO_PHONE_CALL, { patient, visit: eventData.visit })
    }
  }, [patientAnsweredPhoneCall, eventDataRef])

  return null
}
